import { useCallback, useState } from "react";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";
import { fieldOptionRetrieve, useCaching } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";

import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";

import FieldDropdownMenu from "entities/Field/components/FieldDropdownMenu";
import EditButton from "entities/Field/components/Radiobuttonfield/EditButton";
import FieldTitle from "entities/Field/components/Title";
import { EFieldStatus } from "entities/Field/constants";
import { fieldValueCreate, IField } from "entities/Field/sdk";
import { IFieldOption } from "entities/Package/sdk";
import DueDiligenceFieldOpenTaskButton from "entities/Task/components/DueDiligenceFieldOpenTaskButton";
import { ITaskChannel } from "entities/Task/sdk";

export interface IRadiobuttonfield {
  editable: boolean;
  field: IField;
  taskChannels: Array<ITaskChannel>;
  updatePackage: () => void;
  dealId: number;
  packageId: number;
  categoryId: number;
  tabId: number;
  sectionId: number;
  onUpdate: ({ message }: { message?: string }) => void;
  isFocused?: boolean;
}

const Radiobuttonfield = ({
  editable,
  dealId,
  taskChannels,
  updatePackage,
  field,
  packageId,
  categoryId,
  tabId,
  sectionId,
  onUpdate,
  isFocused = false,
}: IRadiobuttonfield) => {
  const [isOpen, setIsOpen] = useState(false);

  const [value, setValue] = useCaching<IFieldOption | null>(
    fieldOptionRetrieve({
      options: field.options,
      value: _.get(field.custom_values, "[0].selected_value"),
    })
  );

  const handleEditButtonClick = useCallback(() => setIsOpen(true), [setIsOpen]);

  const handleSave = useCallback(
    ({ newValue }: { newValue: IFieldOption | null }) => {
      setValue(newValue);
      fieldValueCreate({
        dealId,
        companyCustomFieldId: field.field_id,
        value: newValue?.value || null,
      })
        .then(() => {
          setIsOpen(false);
          onUpdate({ message: `${field.display_name} successfully updated!` });
        })
        .catch(handleInvalidRequest);
    },
    [field, dealId, setValue, onUpdate]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setValue(
      fieldOptionRetrieve({
        options: field.options,
        value: _.get(field.custom_values, "[0].selected_value"),
      })
    );
  }, [setValue, field]);

  return (
    <Stack
      id={field.field_id.toString()}
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{
        padding: theme.spacing(0.5),
        borderBottom: `1px solid ${colors.blue20}`,
        backgroundColor: isFocused ? colors.green10 : "initial",
      }}
      data-testid="dd-field"
    >
      <FieldTitle
        field={field}
        dealId={dealId}
        packageId={packageId}
        onUpdate={onUpdate}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        maxWidth="40%"
        paddingTop={0.5}
      >
        {taskChannels.length > 0 && (
          <DueDiligenceFieldOpenTaskButton
            taskChannels={taskChannels}
            sectionId={sectionId}
            categoryId={categoryId}
            company_field={field}
            dealId={dealId}
            tabId={tabId}
            packageId={packageId}
            taskButtonDataTestid="dd-open-task-button"
            taskMessagesButtonDataTestid="dd-open-new-task-messages-button"
          />
        )}
        {field.status === EFieldStatus.NA ? (
          <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
            N/A
          </Text>
        ) : (
          <>
            {!value && (
              <Tag
                label="missing"
                variant="red"
                dataTestid="empty-field-value"
              />
            )}
            {value && (
              <Text variant="text2" data-testid="field-value">
                {value.label}
              </Text>
            )}
            {editable && (
              <EditButton
                isOpen={isOpen}
                name={field.display_name}
                options={field.options}
                initialValue={value}
                onSave={handleSave}
                onCancel={handleCancel}
                onClick={handleEditButtonClick}
                buttonDataTestid="field-edit-button"
              />
            )}
          </>
        )}
        <FieldDropdownMenu
          field={field}
          dealId={dealId}
          isHidden={false}
          packageId={packageId}
          categoryId={categoryId}
          tabId={tabId}
          sectionId={sectionId}
          onUpdate={onUpdate}
          menuButtonDataTestid="dd-field-actions-button"
        />
      </Stack>
    </Stack>
  );
};

export default Radiobuttonfield;
